import React, { useState, useEffect } from 'react';
import SecureFileImg from '../SecureFileImg';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { useParams, useNavigate } from 'react-router-dom';
import { labelValidasi } from '../../utils/labelValidasi';
import { toProtectedFileUrl } from '../../utils/secureFile';
import {
  Box, Typography, Chip, CircularProgress, Alert, Snackbar, Stack, useMediaQuery,
  Accordion, AccordionSummary, AccordionDetails, Grid, Paper, Button, LinearProgress
} from '@mui/material';
import {
  ArrowBack, Engineering, Payments, Receipt,
  ExpandMore, Person, AttachMoney,
  Check
} from '@mui/icons-material';
import { getMonitoringDetail } from '../API/bantuan';
import { mergeAdministrasiProgress, calcAdmOnlyPercent, getAdmFileUrl } from '../../utils/admProgress';
import FotoGallery from './FotoGallery';
import { getDocumentLabels, getFotoLabels } from '../../utils/stepTemplates';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const STATUS_PROSES = ['Belum Dimulai', 'Sedang Berlangsung', 'Menunggu Verifikasi', 'Selesai', 'Ditolak', 'Dibatalkan'];
const STATUS_PROGRESS = ['Belum Dimulai', 'Sedang Berlangsung', 'Menunggu Verifikasi', 'Selesai'];

const statusColor = (status) => {
  switch (status) {
    case 'Selesai': return 'success';
    case 'Sedang Berlangsung': return 'warning';
    case 'Menunggu Verifikasi': return 'info';
    case 'Belum Dimulai': return 'default';
    default: return 'default';
  }
};

const formatRp = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return '-';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const calcProgressPercent = (steps) => {
  if (!steps?.length) return { presentase: 0, langkahSelesai: 0, totalLangkah: 0 };
  const totalBobot = steps.reduce((s, l) => s + (Number(l.bobot) || 0), 0);
  if (totalBobot > 0) {
    const bobotSelesai = steps.filter(l => l.status === 'Selesai').reduce((s, l) => s + (Number(l.bobot) || 0), 0);
    return { presentase: Math.round((bobotSelesai / totalBobot) * 100), langkahSelesai: steps.filter(l => l.status === 'Selesai').length, totalLangkah: steps.length };
  }
  const selesai = steps.filter(l => l.status === 'Selesai').length;
  return { presentase: Math.round((selesai / steps.length) * 100), langkahSelesai: selesai, totalLangkah: steps.length };
};

const ProgressCard = ({ icon, label, pct, color = 'primary' }) => (
  <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', height: '100%' }}>
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
    </Stack>
    <Typography variant="h6" fontWeight={700} color={`${color}.main`}>{pct.presentase}%</Typography>
    <LinearProgress variant="determinate" value={pct.presentase} sx={{ height: 4, borderRadius: 2, mt: 0.5 }} color={color} />
    <Typography variant="caption" color="text.secondary">{pct.langkahSelesai}/{pct.totalLangkah} selesai</Typography>
  </Paper>
);

const DetailBantuan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:800px)');

  const [loading, setLoading] = useState(true);
  const [bantuan, setBantuan] = useState(null);
  const [pemohon, setPemohon] = useState(null);
  const [langkah, setLangkah] = useState([]);
  const [presentase, setPresentase] = useState(null);
  const [rabData, setRabData] = useState(null);
  const [admBantuan, setAdmBantuan] = useState(null);
  const [progressKeuangan, setProgressKeuangan] = useState([]);
  const [progressFisik, setProgressFisik] = useState([]);
  const [progressAdministrasi, setProgressAdministrasi] = useState([]);
  const [pembelianSupplier, setPembelianSupplier] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getMonitoringDetail(id);
      const data = res.data;
      setBantuan(data.bantuan || data);
      setPemohon(data.pemohon || data.bantuan?.pemohon || null);
      setLangkah(data.langkah || []);
      setPresentase(data.presentase || data.Presentase || null);
      setRabData(data.rab || null);
      setAdmBantuan(data.admBantuan || null);
      setProgressKeuangan(data.progressKeuangan || []);
      setProgressFisik(data.progressFisik || []);
      setProgressAdministrasi(data.progressAdministrasi || []);
      setPembelianSupplier(data.pembelianSupplier || null);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, msg: 'Gagal memuat detail', sev: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) fetchData(); }, [id]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (!bantuan) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="warning">Data bantuan tidak ditemukan.</Alert>
      <Button onClick={() => navigate('/data-bantuan')} sx={{ mt: 2 }}><ArrowBack /> Kembali</Button>
    </Box>
  );

  const mergedAdministrasi = mergeAdministrasiProgress(progressAdministrasi, admBantuan);
  const pctAdm = progressAdministrasi.length > 0 ? calcProgressPercent(mergedAdministrasi) : calcAdmOnlyPercent(admBantuan);
  const pctKeu = calcProgressPercent(progressKeuangan);
  const pctFisik = calcProgressPercent(progressFisik);

  const displaySteps = mergedAdministrasi.length > 0 ? mergedAdministrasi
    : (langkah && langkah.length > 0 ? langkah.map(l => ({ ...l })) : []);

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };
  const urlRumah = `${BASE_URL}/`;
  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/data-bantuan')} sx={{ mb: 2 }}>
          Kembali ke Data Penerima Bantuan
        </Button>

        {/* Header */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#0A5EB0', color: 'white' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {pemohon?.nama || bantuan?.namaLengkap || 'Penerima Bantuan'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                #{bantuan.id} · {bantuan.JenisBantuan || bantuan.bantuan?.JenisBantuan}
              </Typography>
            </Box>
            <Chip label={bantuan.ProsesBantuan?.statusProses || bantuan.bantuan?.ProsesBantuan?.statusProses || '-'}
              color={statusColor(bantuan.ProsesBantuan?.statusProses || bantuan.bantuan?.ProsesBantuan?.statusProses)}
              sx={{ fontWeight: 600 }} />
          </Stack>
        </Paper>

        {/* Info Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Nominal</Typography>
              <Typography variant="body1" fontWeight={700} color="primary.main">
                {formatRp(bantuan.nominal || bantuan.bantuan?.nominal)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="body1" fontWeight={700}>
                {presentase?.presentase || 0}%
              </Typography>
              <LinearProgress variant="determinate" value={presentase?.presentase || 0} sx={{ height: 4, borderRadius: 2, mt: 0.5 }} />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Kecamatan</Typography>
              <Typography variant="body2" fontWeight={600}>{pemohon?.kecamatan || '-'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Desa/Kelurahan</Typography>
              <Typography variant="body2" fontWeight={600}>{pemohon?.desaKelurahan || '-'}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Detail Penerima */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight={600}><Person sx={{ mr: 1, verticalAlign: 'middle' }} />Informasi Penerima</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Nama</Typography><Typography variant="body2" fontWeight={500}>{pemohon?.nama || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">NIK</Typography><Typography variant="body2">{bantuan?.Questionnaire?.nomorKTP  || bantuan?.Verifikasi?.nomorKTP || bantuan?.nomorKTP || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Alamat</Typography><Typography variant="body2">{pemohon?.alamat || bantuan?.alamatLengkap || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Desa/Kelurahan</Typography><Typography variant="body2">{pemohon?.desaKelurahan || bantuan?.desaKelurahan || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Kecamatan</Typography><Typography variant="body2">{pemohon?.kecamatan || bantuan?.kecamatan || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">No. KK</Typography><Typography variant="body2">{bantuan?.Questionnaire?.nomorKK || bantuan?.Verifikasi?.nomorKK || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">No. WA</Typography><Typography variant="body2">{pemohon?.noWaPenerima || bantuan?.noWaPenerimaBantuan || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">No. Rekening</Typography><Typography variant="body2">{pemohon?.noRekening || bantuan?.noRekening || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Skor</Typography><Typography variant="body2">{pemohon?.score != null ? pemohon.score : '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Kategori</Typography><Typography variant="body2">{pemohon?.kategori || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">TFL Pendamping</Typography><Typography variant="body2">{bantuan?.namaTFL || pemohon?.namaTFL || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">AGR Pendamping</Typography><Typography variant="body2">{bantuan?.namaAgr || pemohon?.namaAgr || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Supplier</Typography><Typography variant="body2">{bantuan?.BantuanSuppliers?.map(s => s.SupplierBarang?.namaSupplier).filter(Boolean).join(', ') || pembelianSupplier?.pembelianPerSupplier?.map(s => s.namaSupplier).join(', ') || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Status Kontrak</Typography><Typography variant="body2">{bantuan?.statusKontrak || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Nominal Bantuan</Typography><Typography variant="body2" fontWeight={600} color="primary.main">{formatRp(bantuan?.nominal)}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Tanggal Bantuan</Typography><Typography variant="body2">{formatDate(bantuan?.tanggalBantuan)}</Typography></Grid>
             <Grid item xs={12} sm={6}>
  <Typography variant="caption" color="text.secondary">
    Foto Rumah Penerima Bantuan
  </Typography>
  {bantuan?.Questionnaire?.fotos && bantuan.Questionnaire.fotos.length > 0 ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      {bantuan.Questionnaire.fotos.map((foto, index) => {
        // Replace backslash dengan forward slash dan hapus 'uploads/images/' jika ada
        let fotoPath = foto.foto_rumah.replace(/\\/g, '/');
        // Jika path sudah mengandung 'uploads/images/', gunakan langsung
        // Jika tidak, tambahkan prefix
        const imageUrl = fotoPath.startsWith('uploads/') 
          ? `${BASE_URL}/${fotoPath}`
          : `${BASE_URL}/files/images/${fotoPath}`;
        
        return (
          <img
            key={foto.id || index}
            src={imageUrl}
            alt={`Foto Rumah ${index + 1}`}
            style={{
              width: '150px',
              height: '150px',
              objectFit: 'cover',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
            onClick={() => openInAppFilePreview({ url: imageUrl })}
            onError={(e) => {
              e.target.style.display = 'none';
              // Tampilkan pesan error
              const parent = e.target.parentElement;
              const errorMsg = document.createElement('div');
              errorMsg.textContent = 'Gagal memuat gambar';
              errorMsg.style.color = '#999';
              errorMsg.style.fontSize = '12px';
              parent.appendChild(errorMsg);
            }}
          />
        );
      })}
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Tidak ada foto
    </Typography>
  )}
</Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Progress Ringkasan */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Progress per Proses</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}><ProgressCard icon={<Receipt fontSize="small" color="info" />} label="Administrasi" pct={pctAdm} color="info" /></Grid>
          <Grid item xs={6} sm={3}><ProgressCard icon={<AttachMoney fontSize="small" color="warning" />} label="Keuangan" pct={pctKeu} color="warning" /></Grid>
          <Grid item xs={6} sm={3}><ProgressCard icon={<Engineering fontSize="small" color="success" />} label="Fisik" pct={pctFisik} color="success" /></Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', height: '100%' }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Payments fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={600}>RAB</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} color="primary.main">{rabData ? '✓' : '—'}</Typography>
              <Typography variant="caption" color="text.secondary">{rabData ? 'Tersedia' : 'Belum'}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* RAB - read only */}
        <Accordion defaultExpanded={!!rabData}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight={600}><Payments sx={{ mr: 1, verticalAlign: 'middle' }} />RAB</Typography>
            {rabData && <Chip size="small" sx={{ ml: 1 }} label={rabData.status || '-'} color={rabData.status === 'disetujui' ? 'success' : 'default'} />}
          </AccordionSummary>
          <AccordionDetails>
            {rabData ? (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Keterangan:</strong> {rabData.keterangan || '-'}</Typography>
                {rabData.fileRab && (
                  <Button size="small" variant="outlined" onClick={() => openInAppFilePreview({ url: `${BASE_URL}/rab/file/${rabData.fileRab.replace('rab/', '')}` })}>
                    Lihat Berkas RAB
                  </Button>
                )}
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">RAB belum dibuat.</Typography>}
          </AccordionDetails>
        </Accordion>

        {/* Progress Administrasi */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight={600}><Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />A. Progress Administrasi</Typography>
            <Chip size="small" sx={{ ml: 1 }} label={`${pctAdm.presentase}%`} color={pctAdm.presentase >= 100 ? 'success' : 'default'} />
            {displaySteps.length > 0 && <Chip size="small" sx={{ ml: 0.5 }} label={`${displaySteps.filter(l => l.status === 'Selesai').length}/${displaySteps.length}`} variant="outlined" />}
          </AccordionSummary>
          <AccordionDetails>
            {displaySteps.length > 0 ? (
              <Box sx={{ position: 'relative', pl: 2 }}>
                {displaySteps.map((step, idx) => (
                  <Box key={step.id} sx={{ position: 'relative', pb: 2.5 }}>
                    {idx < displaySteps.length - 1 && (
                      <Box sx={{ position: 'absolute', left: -5, top: 24, bottom: 0, width: 2, bgcolor: step.status === 'Selesai' ? 'success.main' : '#e0e0e0' }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: step.status === 'Selesai' ? 'success.main' : step.status === 'Sedang Berlangsung' ? 'warning.main' : '#e0e0e0',
                        color: step.status === 'Belum Dimulai' ? 'text.secondary' : 'white', fontSize: 12, fontWeight: 600, zIndex: 1 }}>
                        {step.status === 'Selesai' ? <Check fontSize="inherit" /> : idx + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>{labelValidasi(step.tahap)}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip size="small" label={`${step.bobot || 0}%`} variant="outlined" sx={{ fontSize: 11 }} />
                            <Chip size="small" color={statusColor(step.status)} label={labelValidasi(step.status)} />
                          </Box>
                        </Box>
                          {step.foto && (() => { try { const f = typeof step.foto === 'string' ? JSON.parse(step.foto) : step.foto; if (Array.isArray(f) && f.length > 0) { const fl = getFotoLabels('fisik', step, f); return (<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>{f.map((x, i) => (<Box key={i} sx={{ textAlign: 'center', maxWidth: 72 }}><SecureFileImg src={toProtectedFileUrl(getAdmFileUrl(x, null, BASE_URL))} alt={fl[i]} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(x, null, BASE_URL)), title: fl[i] })} /><Typography variant="caption" sx={{ display: 'block', fontSize: 9, lineHeight: 1.2, color: 'text.secondary', mt: 0.25 }}>{fl[i]}</Typography></Box>))}</Stack>); } } catch(e){} return null; })()}
                        {step.dokumen && (() => { try { const d = typeof step.dokumen === 'string' ? JSON.parse(step.dokumen) : step.dokumen; if (Array.isArray(d) && d.length > 0) { const labels = getDocumentLabels('administrasi', step, d); return (<Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>{d.map((x, i) => <Chip key={i} size="small" label={labels[i]} variant="outlined" onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(x, null, BASE_URL)) })} sx={{ cursor: 'pointer' }} />)}</Stack>); } } catch(e){} return null; })()}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : <Typography variant="body2" color="text.secondary">Belum ada data administrasi.</Typography>}
          </AccordionDetails>
        </Accordion>

        {/* Progress Keuangan */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight={600}><AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />B. Progress Keuangan</Typography>
            <Chip size="small" sx={{ ml: 1 }} label={`${pctKeu.presentase}%`} />
            {progressKeuangan.length > 0 && <Chip size="small" sx={{ ml: 0.5 }} label={`${progressKeuangan.filter(l => l.status === 'Selesai').length}/${progressKeuangan.length}`} variant="outlined" />}
          </AccordionSummary>
          <AccordionDetails>
            {progressKeuangan.length > 0 ? (
              <Box sx={{ position: 'relative', pl: 2 }}>
                {progressKeuangan.map((step, idx) => (
                  <Box key={step.id} sx={{ position: 'relative', pb: 2.5 }}>
                    {idx < progressKeuangan.length - 1 && (
                      <Box sx={{ position: 'absolute', left: -5, top: 24, bottom: 0, width: 2, bgcolor: step.status === 'Selesai' ? 'success.main' : '#e0e0e0' }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: step.status === 'Selesai' ? 'success.main' : step.status === 'Sedang Berlangsung' ? 'warning.main' : '#e0e0e0',
                        color: step.status === 'Belum Dimulai' ? 'text.secondary' : 'white', fontSize: 12, fontWeight: 600, zIndex: 1 }}>
                        {step.status === 'Selesai' ? <Check fontSize="inherit" /> : idx + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>{labelValidasi(step.tahap)}</Typography>
                          <Chip size="small" label={`${step.bobot}%`} variant="outlined" sx={{ fontSize: 11 }} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : <Typography variant="body2" color="text.secondary">Belum ada data keuangan.</Typography>}
          </AccordionDetails>
        </Accordion>

        {/* Progress Fisik */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight={600}><Engineering sx={{ mr: 1, verticalAlign: 'middle' }} />C. Progress Fisik</Typography>
            <Chip size="small" sx={{ ml: 1 }} label={`${pctFisik.presentase}%`} />
            {progressFisik.length > 0 && <Chip size="small" sx={{ ml: 0.5 }} label={`${progressFisik.filter(l => l.status === 'Selesai').length}/${progressFisik.length}`} variant="outlined" />}
          </AccordionSummary>
          <AccordionDetails>
            {progressFisik.length > 0 ? (
              <Box>
                <Chip size="small" label={`Tipe: ${progressFisik[0]?.tipe || '-'}`} color="primary" sx={{ mb: 1 }} />
                <Box sx={{ position: 'relative', pl: 2 }}>
                  {progressFisik.map((step, idx) => (
                    <Box key={step.id} sx={{ position: 'relative', pb: 2.5 }}>
                      {idx < progressFisik.length - 1 && (
                        <Box sx={{ position: 'absolute', left: -5, top: 24, bottom: 0, width: 2, bgcolor: step.status === 'Selesai' ? 'success.main' : '#e0e0e0' }} />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: step.status === 'Selesai' ? 'success.main' : step.status === 'Sedang Berlangsung' ? 'warning.main' : '#e0e0e0',
                          color: step.status === 'Belum Dimulai' ? 'text.secondary' : 'white', fontSize: 12, fontWeight: 600, zIndex: 1 }}>
                          {step.status === 'Selesai' ? <Check fontSize="inherit" /> : idx + 1}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={500}>{labelValidasi(step.tahap)}</Typography>
                            <Chip size="small" label={`${step.bobot}%`} variant="outlined" sx={{ fontSize: 11 }} />
                          </Box>
                        {step.foto && (() => { try { const f = typeof step.foto === 'string' ? JSON.parse(step.foto) : step.foto; if (Array.isArray(f) && f.length > 0) { const fl = getFotoLabels('administrasi', step, f); return (<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>{f.map((x, i) => (<Box key={i} sx={{ textAlign: 'center', maxWidth: 72 }}><SecureFileImg src={toProtectedFileUrl(getAdmFileUrl(x, null, BASE_URL))} alt={fl[i]} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(x, null, BASE_URL)), title: fl[i] })} /><Typography variant="caption" sx={{ display: 'block', fontSize: 9, lineHeight: 1.2, color: 'text.secondary', mt: 0.25 }}>{fl[i]}</Typography></Box>))}</Stack>); } } catch(e){} return null; })()}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : <Typography variant="body2" color="text.secondary">Belum ada data fisik.</Typography>}
          </AccordionDetails>
        </Accordion>

        {/* Foto Proses */}
        <Box sx={{ mt: 2 }}>
          <FotoGallery
            langkah={progressFisik}
            title="Foto Proses Pengerjaan"
            fileBase="rab-progress-fisik"
          />
        </Box>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.sev} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default DetailBantuan;
